import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum, SectionEnum, WorkOrderVersionActKc2VersionData } from 'gql/graphql';
import type { IWorkOrderSummarySelectItem } from 'pages/Project/UerProject/WorkOrder/WorkOrderSummary';
import { chapterWorkOrderSummarySelectItem } from 'pages/Project/UerProject/WorkOrder/WorkOrderSummary';
import { Loader, Button, IconRestart, Select } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { gqlWorkOrderVersionObjectResults } from '../workOrderVersionQueries';

import { WorkOrderVersionSummaryItem } from './WorkOrderVersionSummaryItem';

import Styles from './WorkOrderVersionSummary.module.css';

interface IWorkOrderVersionSummaryProps {
    chapter?: ChapterEnum | null;
    section?: SectionEnum;
    workOrderObjectId: number;
    isVisible: boolean;
    actsKc2?: WorkOrderVersionActKc2VersionData[] | null;
}

export const WorkOrderVersionSummary: React.FC<IWorkOrderVersionSummaryProps> = (props) => {
    const { versionId } = useParams();
    const { chapter, section, workOrderObjectId, isVisible, actsKc2 } = props;
    const dispatch = useDispatch();

    const summaryItems: IWorkOrderSummarySelectItem[] = useMemo(
        () =>
            (actsKc2 ?? []).map((act) => ({
                id: act.id,
                label: `Итоги Акта${descriptionNumberAndDate(act.number, act.date)}`,
            })),
        [actsKc2],
    );

    const variables = useMemo(
        () => ({
            chapter,
            section,
            workOrderVersionId: Number(versionId),
            workOrderVersionObjectId: workOrderObjectId,
        }),
        [chapter, section, versionId, workOrderObjectId],
    );

    const [workOrderObjectResultData, workOrderObjectResultStatus, , workOrderObjectResultMakeRequest] = useRequest({
        requestId: 'workOrderObjectResult',
        query: gqlWorkOrderVersionObjectResults,
        variables,
        withEffect: false,
    });

    const isLoading = workOrderObjectResultStatus === RequestStatus.FETCHING;

    const [selectedSummaryItem, setSelectedSummaryItem] = useState<IWorkOrderSummarySelectItem | null>(
        chapterWorkOrderSummarySelectItem,
    );

    const onDataRefreshRequest = useCallback(() => {
        dispatch(workOrderObjectResultMakeRequest(variables));
    }, [dispatch, variables, workOrderObjectResultMakeRequest]);

    const onSelectedSummaryItemChange = useCallback(
        (selectedItem) => {
            if (selectedItem !== selectedSummaryItem) {
                setSelectedSummaryItem(selectedItem);
            }
        },
        [selectedSummaryItem],
    );

    useEffect(() => {
        if (isVisible) {
            onDataRefreshRequest();
        }
    }, [isVisible, onDataRefreshRequest]);

    const actualData = useMemo(
        () =>
            workOrderObjectResultData?.uerWorkOrderVersionObjectResults?.filter((item) =>
                selectedSummaryItem?.id === chapterWorkOrderSummarySelectItem.id
                    ? item.actKC2VersionId === null
                    : item.actKC2VersionId === Number(selectedSummaryItem?.id),
            ),
        [selectedSummaryItem?.id, workOrderObjectResultData],
    );

    const items = useMemo(() => [chapterWorkOrderSummarySelectItem, ...summaryItems], [summaryItems]);

    const onChange = useCallback(
        ({ value }) => {
            onSelectedSummaryItemChange(value);
        },
        [onSelectedSummaryItemChange],
    );

    return (
        <div className={Styles.workOrderSummaryContainer}>
            <div className={Styles.workOrderSummaryHeader}>
                <Select
                    size="s"
                    items={items}
                    value={selectedSummaryItem}
                    onChange={onChange}
                    className={Styles.workOrderSummaryHeaderCombo}
                />
                <Button
                    iconLeft={IconRestart}
                    disabled={isLoading}
                    view="secondary"
                    onClick={onDataRefreshRequest}
                    size="s"
                />
            </div>
            <div className={Styles.workOrderSummaryContent}>
                {isLoading && <Loader className={Styles.workOrderSummaryLoader} />}
                {!isLoading &&
                    actualData?.map((item, index) => <WorkOrderVersionSummaryItem item={item} key={index} />)}
            </div>
        </div>
    );
};

WorkOrderVersionSummary.displayName = 'WorkOrderVersionSummary';
